import { Component, ReactNode } from 'react'
import * as styles from 'components/features/pfm/CreditLinkage/CreditLinkage.css'
import CreditFormContainer from 'components/features/pfm/CreditForm/CreditFormContainer'
import CreditLinkageQuestions from 'components/molecules/CreditLinkageQuestions'
import Button from 'components/features/pfm/Button/Button'
import Spinner from 'components/features/pfm/Spinner/Spinner'
import TransunionBadge from 'components/atoms/TransunionBadge'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import GatherHeader from 'components/features/pfm/GatherHeader/GatherHeader'
import { CONNECTION_ERROR_TEXT, TUCI_ERRORS_MAP } from 'components/atoms/Errors'
import { captureWarning, UNRECOGNIZED_TUCI_ERROR } from 'helpers/errorHelpers'
import { isBlockingError } from 'helpers/creditHelpers'
import { CreditLinkagePhase } from 'interfaces/credit'
import { STEPS_COUNT_WITH_FORM } from 'constants/onboarding'

export type CreditLinkageStateProps = {
  phase: CreditLinkagePhase
  isFormSubmitting: boolean
  hasNotAnsweredQuestions: boolean

  isDisabled: boolean
  error: string | null

  isSkipLoading: boolean
  isSkipped: boolean
}

export type CreditLinkageDispatchProps = {
  retryCredit: () => void
  skipCredit: () => void
  resetCredit: () => void
}

export type CreditLinkageOwnProps = {
  // makes skippable and sets size of form
  step?: number
  title: ReactNode
}

type CreditLinkageProps = CreditLinkageStateProps & CreditLinkageDispatchProps & CreditLinkageOwnProps

class CreditLinkage extends Component<CreditLinkageProps> {

  render () {
    const { error, title, step } = this.props

    const content = error ? this.renderError(error) : this.renderContent()

    return (
      <section className={ styles.creditLinkage }>
        <GatherHeader title={ title } step={ step } maxStep={ STEPS_COUNT_WITH_FORM }>
          { this.renderHeader() }
        </GatherHeader>
        { content }
      </section>
    )
  }

  renderContent () {
    const {
      isDisabled,
      phase,
      isFormSubmitting,
      hasNotAnsweredQuestions,
    } = this.props

    const shouldShowPreloader = isFormSubmitting || (phase === 'questions' && !hasNotAnsweredQuestions)

    if (shouldShowPreloader) {
      return (
        <div className={ styles.spinner }>
          <Spinner size='medium' />
        </div>
      )
    }

    const mainContent = phase === 'questions' && !isDisabled
      ? <CreditLinkageQuestions />
      : (
        <CreditFormContainer
          isDisabled={ isDisabled }
          theme='snow'
        />)

    const transunion = phase !== 'questions' && !isFormSubmitting && (
      <div className={ styles.transunionBadge }>
        <TransunionBadge />
      </div>
    )

    return (
      <section className={ styles.content }>
        { mainContent }
        { transunion }
      </section>
    )
  }

  renderHeader () {
    const {
      phase, error,
      isFormSubmitting, hasNotAnsweredQuestions,
    } = this.props

    if (error) {
      let errorText = TUCI_ERRORS_MAP[error]
      if (!errorText) {
        captureWarning(UNRECOGNIZED_TUCI_ERROR, {
          errorCode: error,
        })
        errorText = CONNECTION_ERROR_TEXT
      }

      return errorText
    }

    if (phase === 'questions') {
      return hasNotAnsweredQuestions
        ? (
          <section className={ styles.subtitleHighlighted } key='questions'>
            Your security is very important to us.<br />
            Please answer the security questions below to verify your identity.
          </section>
        ) : (
          <section className={ styles.subtitleHighlighted } key='questions'>
            Validating the answers…
          </section>
        )
    }

    if (phase === 'form-extended') {
      return isFormSubmitting
        ? (
          <section className={ styles.subtitleHighlighted } key='extended'>
            We are verifying your identity and linking your credit file.
          </section>
        ) : (
          <section className={ styles.subtitleHighlighted } key='extended' data-qa-marker='not-able-verify-identity'>
            We are not able to verify your identity using the information you provided.<br />
            Please verify the information below and enter
            your Social Security Number to finish linking your credit file.
          </section>
        )
    }

    return (
      <section>
        <p className={ styles.subheaderParagraph }>
          This activates free credit report monitoring and enables Status to analyze your interest rates.
          Linking does not affect your credit score.
        </p>
        <p className={ styles.subheaderParagraph }>
          You'll earn a $1 reward for linking your credit file.
        </p>
      </section>
    )
  }

  renderError (error: string) {
    const {
      retryCredit, resetCredit,
      isSkipLoading,
    } = this.props

    const isUserBlocked = isBlockingError(error)
    let errorText = TUCI_ERRORS_MAP[error]
    if (!errorText) {
      captureWarning(UNRECOGNIZED_TUCI_ERROR, {
        errorCode: error,
      })
      errorText = CONNECTION_ERROR_TEXT
    }

    // ONBOARDING: retry (if user's not blocked) and skip
    // CREDIT-LINKAGE: if user's blocked - toDashboard, else - retry
    const toDashboardButton = isUserBlocked && (
      <section className={ styles.errorButtonContainer }>
        <NavigationLink to='dashboard'>
          <Button
            size='small'
            theme='blue'
            fullWidth
            onClick={ resetCredit }
          >
            Return to Dashboard
          </Button>
        </NavigationLink>
      </section>
    )
    const retryButton = !isUserBlocked && (
      <section className={ styles.errorButtonContainer }>
        <Button
          qaMarker='retry-button'
          size='small'
          theme='blue'
          fullWidth
          disabled={ isSkipLoading }
          onClick={ retryCredit }
        >
          Retry
        </Button>
      </section>
    )

    return (
      <section className={ styles.errorButtons }>
        { toDashboardButton }
        { retryButton }
      </section>
    )
  }
}

export default CreditLinkage
